import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import cn from '../../utils/cn';
import { useReserhubAccount } from '../../context/ReserhubAccount';
import AdultIcon from '../../images/portfolio.webp';
import useWhitelabelEnvs from '../../hooks/whitelabel/useWhitelabelEnvs';
import CSSVariablesProvider from '../CSSVariablesProvider';

/**
 * UserMenuSheet component.
 * Root component that manages the sheet state.
 * @param {object} props - open, onOpenChange, children, and all props from Sheet component.
 * @param {boolean} [props.open] - Whether the sheet is open.
 * @param {Function} [props.onOpenChange] - Callback when open state changes.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element}
 */
export function UserMenuSheet({ open, onOpenChange, children }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {children}
    </Sheet>
  );
}

UserMenuSheet.propTypes = {
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  children: PropTypes.node.isRequired,
};

UserMenuSheet.defaultProps = {
  open: undefined,
  onOpenChange: undefined,
};

/**
 * UserMenuSheetTrigger component.
 * Component that triggers the sheet to open.
 * @param {object} props - asChild, children, and all props from SheetTrigger component.
 * @param {boolean} [props.asChild] - Whether to render as child.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element}
 */
export function UserMenuSheetTrigger({ asChild, children }) {
  return <SheetTrigger asChild={asChild}>{children}</SheetTrigger>;
}

UserMenuSheetTrigger.propTypes = {
  asChild: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

UserMenuSheetTrigger.defaultProps = {
  asChild: undefined,
};

/**
 * UserMenuSheetContent component.
 * Content component with user info section.
 * @param {object} props - children, className.
 * @param {React.ReactNode} props.children - Child components.
 * @param {string} props.className - Additional CSS classes.
 * @returns {JSX.Element}
 */
export function UserMenuSheetContent({ children, className }) {
  const { t } = useTranslation('loyalty');
  const { user } = useReserhubAccount();

  const { theme, brand } = useWhitelabelEnvs();
  const { FUNNEL_STYLE } = useWhitelabelFeatures();

  const avatar = user?.avatar || AdultIcon;

  const userName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

  return (
    <SheetContent
      side="right"
      className={cn(
        'bg-white/30 w-full overflow-y-auto p-3 pt-14 shadow-2xl md:rounded-l-3xl md:max-w-[420px]',
        className,
      )}
    >
      <CSSVariablesProvider
        theme={theme}
        funnelStyle={FUNNEL_STYLE}
        funnelStyleBrandVariation={brand.toUpperCase()}
      >
        {/* Visually hidden elements for accessibility */}
        <SheetTitle className="sr-only">{t('buttons.user_menu_aria_label')}</SheetTitle>
        <SheetDescription className="sr-only">
          {t('buttons.menu_button_aria_label')}
        </SheetDescription>

        <div className="bg-white mt-6 flex flex-1 flex-col overflow-y-auto rounded-3xl px-4 py-11 pb-11">
          {/* User Avatar Section */}
          <div className="bg-primary-regular/30 absolute left-1/2 -mt-28 -translate-x-1/2 rounded-full p-3 backdrop-blur-3xl">
            <div className="flex items-center justify-center size-20 rounded-full bg-gradient-primary p-1">
              <img src={avatar} alt="" className="size-full rounded-full object-cover" />
            </div>
          </div>

          {/* User Info */}
          <div className="mb-7 flex-shrink-0 text-center">
            <p className="text-gray-600 text-lg font-semibold">{userName}</p>
          </div>

          {children}
        </div>
      </CSSVariablesProvider>
    </SheetContent>
  );
}

UserMenuSheetContent.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

UserMenuSheetContent.defaultProps = {
  className: '',
};

/**
 * UserMenuSheetClose component.
 * Component that closes the sheet when clicked.
 * @param {object} props - asChild, children, and all props from SheetClose component.
 * @param {boolean} [props.asChild] - Whether to render as child.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element}
 */
export function UserMenuSheetClose({ asChild, children }) {
  return <SheetClose asChild={asChild}>{children}</SheetClose>;
}

UserMenuSheetClose.propTypes = {
  asChild: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

UserMenuSheetClose.defaultProps = {
  asChild: undefined,
};
